import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { cn, formatCurrency } from '@/lib/utils';
import { toast } from 'sonner';
import {
  Calculator,
  Upload,
  Loader2,
  FileImage,
  Bot,
  Plus,
  X,
} from 'lucide-react';
import type { ApiResponse, Estimate } from '@/types';

export function EstimatesPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const [isUploadZone, setIsUploadZone] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showNewEstimate, setShowNewEstimate] = useState(false);
  const [estimateName, setEstimateName] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState('');

  const { data: estimates, isLoading } = useQuery({
    queryKey: ['estimates'],
    queryFn: async (): Promise<Estimate[]> => {
      const res: ApiResponse<Estimate[]> = await api.get('/estimates');
      return res.success ? (res.data ?? []) : [];
    },
  });

  const { data: projects } = useQuery({
    queryKey: ['projects-for-estimate'],
    queryFn: async (): Promise<{ id: string; name: string }[]> => {
      const res = await api.get<{ id: string; name: string }[]>('/projects');
      return res.success ? (res.data ?? []) : [];
    },
  });

  const handleFileDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsUploadZone(false);
    const files = Array.from(e.dataTransfer.files).filter(
      f => f.type.startsWith('image/') || f.type === 'application/pdf'
    );
    setUploadedFiles(prev => [...prev, ...files]);
    if (files.length > 0) {
      toast.info(`${files.length} file(s) added for AI takeoff`);
    }
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    setUploadedFiles(prev => [...prev, ...files]);
    if (files.length > 0) {
      toast.info(`${files.length} file(s) added for AI takeoff`);
    }
  };

  const removeFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleAIEstimate = async () => {
    if (uploadedFiles.length === 0) {
      toast.error('Please upload at least one floor plan');
      return;
    }
    if (!estimateName.trim()) {
      toast.error('Please enter an estimate name');
      return;
    }

    setIsProcessing(true);
    try {
      const res = await api.post('/estimates/ai-takeoff', {
        name: estimateName,
        projectId: selectedProjectId || undefined,
        fileNames: uploadedFiles.map(f => f.name),
      });
      if (res.success) {
        toast.success('AI estimation started! Processing your floor plans...');
        setUploadedFiles([]);
        setEstimateName('');
        setShowNewEstimate(false);
        queryClient.invalidateQueries({ queryKey: ['estimates'] });
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'AI estimation failed');
    } finally {
      setIsProcessing(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'draft': return <Badge variant="outline">Draft</Badge>;
      case 'active': return <Badge variant="default">Active</Badge>;
      case 'archived': return <Badge variant="secondary">Archived</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Estimates</h1>
          <p className="text-muted-foreground">AI-powered cost estimates and quotes</p>
        </div>
        <Button onClick={() => setShowNewEstimate(true)}>
          <Plus className="mr-2 h-4 w-4" /> New Estimate
        </Button>
      </div>

      {/* AI Takeoff Upload Zone */}
      <Card className="border-dashed border-primary/50">
        <CardContent className="p-6">
          <div
            className={cn(
              'flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 transition-colors',
              isUploadZone ? 'border-primary bg-primary/5' : 'border-border',
            )}
            onDragOver={(e) => { e.preventDefault(); setIsUploadZone(true); }}
            onDragLeave={() => setIsUploadZone(false)}
            onDrop={handleFileDrop}
          >
            <Bot className="mb-3 h-10 w-10 text-primary opacity-80" />
            <h3 className="text-lg font-semibold">AI Takeoff</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Drop floor plans or blueprints here for AI-powered estimation
            </p>
            <p className="text-xs text-muted-foreground">
              Supports PDF, PNG, JPG — up to 20MB each
            </p>
            <label className="mt-4 cursor-pointer">
              <Button variant="outline" size="sm" asChild>
                <span>
                  <Upload className="mr-2 h-4 w-4" />
                  Select Files
                </span>
              </Button>
              <input
                type="file"
                className="hidden"
                accept="image/*,.pdf"
                multiple
                onChange={handleFileSelect}
              />
            </label>
          </div>

          {/* Uploaded files preview */}
          {uploadedFiles.length > 0 && (
            <div className="mt-4 space-y-2">
              <p className="text-sm font-medium">Files to process ({uploadedFiles.length})</p>
              {uploadedFiles.map((file, i) => (
                <div key={i} className="flex items-center justify-between rounded-md border p-2 text-sm">
                  <div className="flex items-center gap-2">
                    <FileImage className="h-4 w-4 text-muted-foreground" />
                    <span className="truncate">{file.name}</span>
                    <span className="text-xs text-muted-foreground">
                      ({(file.size / 1024 / 1024).toFixed(1)} MB)
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => removeFile(i)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Estimates List */}
      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      ) : (estimates?.length ?? 0) === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Calculator className="mx-auto mb-4 h-12 w-12 text-muted-foreground opacity-50" />
            <h3 className="text-lg font-medium">No estimates yet</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Upload floor plans above or create a manual estimate
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {estimates?.map((est) => (
            <Card key={est.id} className="transition-all hover:border-primary/50">
              <CardContent className="flex items-center justify-between p-4">
                <div className="min-w-0 flex-1">
                  <h4 className="font-medium">{est.name}</h4>
                  <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
                    <Badge variant="secondary" className="text-xs">
                      {est.type === 'ai_takeoff' ? 'AI Takeoff' : est.type}
                    </Badge>
                    <span>Margin: {est.marginPct ?? '—'}%</span>
                    <span>Items: {est.lineItems?.length ?? 0}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {getStatusBadge(est.status)}
                  <span className="text-xs text-muted-foreground">
                    {new Date(est.createdAt).toLocaleDateString('en-AU')}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* New Estimate Dialog */}
      <Dialog open={showNewEstimate} onOpenChange={setShowNewEstimate}>
        <DialogHeader>
          <DialogTitle>New AI Estimate</DialogTitle>
          <DialogDescription>
            Upload floor plans for AI-powered takeoff and cost estimation
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="estimateName">Estimate Name *</Label>
            <Input
              id="estimateName"
              value={estimateName}
              onChange={(e) => setEstimateName(e.target.value)}
              placeholder="Ground Floor Takeoff"
            />
          </div>
          {uploadedFiles.length > 0 && (
            <div className="text-sm">
              <span className="font-medium">{uploadedFiles.length} file(s)</span> ready for processing
            </div>
          )}
          {uploadedFiles.length === 0 && (
            <div className="rounded-lg border border-dashed p-4 text-center">
              <p className="text-sm text-muted-foreground">
                Drag and drop floor plans in the upload zone above, then open this dialog to start
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setShowNewEstimate(false)}>
            Cancel
          </Button>
          <Button onClick={handleAIEstimate} disabled={isProcessing || uploadedFiles.length === 0}>
            {isProcessing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Bot className="mr-2 h-4 w-4" />}
            Start AI Takeoff
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  );
}
